<template>
  <div id="app">
    <header class="main-header">
      <div class="top-bar container">
        <nav class="main-navigation">
          <router-link to="/">Etusivu</router-link>
          <router-link v-if="isAuthenticated" to="/team">Oma Remmi</router-link>
          <router-link v-if="isAuthenticated" to="/results">Sijoitukset</router-link>
          <router-link v-if="isAuthenticated" to="/group">Ryhmä</router-link>
          <router-link to="/introduction">Ohjeet</router-link>
        </nav>

        <div class="header-status-stack" v-if="gameProgressDisplay || changeWindowDisplay">
          <div class="game-progress-display" v-if="gameProgressDisplay">
            {{ gameProgressDisplay }}
          </div>
          <div class="change-window-display" v-if="changeWindowDisplay">
            {{ changeWindowDisplay }}
          </div>
        </div>

        <div class="auth-links">
            <!-- User dropdown when authenticated -->
            <div v-if="isAuthenticated && username" class="user-dropdown" ref="userDropdown">
              <button @click="toggleDropdown" class="username-button" :class="{ 'dropdown-open': dropdownOpen }">
                <span class="username-text">{{ username }}</span>
                <svg class="dropdown-arrow" :class="{ 'rotated': dropdownOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              
              <div v-if="dropdownOpen" class="dropdown-menu">
                <router-link to="/profile" class="dropdown-item" @click="closeDropdown">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="dropdown-icon">
                    <path d="M8 8C10.2091 8 12 6.20914 12 4C12 1.79086 10.2091 0 8 0C5.79086 0 4 1.79086 4 4C4 6.20914 5.79086 8 8 8Z" fill="currentColor"/>
                    <path d="M8 10C4.68629 10 2 12.6863 2 16H14C14 12.6863 11.3137 10 8 10Z" fill="currentColor"/>
                  </svg>
                  Oma Profiili
                </router-link>
                <div class="dropdown-divider"></div>
                <button @click="handleSignOut" class="dropdown-item sign-out-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" class="dropdown-icon">
                    <path d="M6 2H3C2.44772 2 2 2.44772 2 3V13C2 13.5523 2.44772 14 3 14H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M11 11L14 8L11 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M14 8H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  Kirjaudu ulos
                </button>
              </div>
            </div>
            
            <!-- Auth buttons when not authenticated -->
            <button v-if="!isAuthenticated" @click="goToSignIn" class="btn btn-outline">Kirjaudu</button>
            <button v-if="!isAuthenticated" @click="goToSignUp" class="btn btn-outline">Rekisteröidy</button>
        </div>
      </div>

      <div class="header-content-wrapper">
        <div class="header-content container">
          <img src="@/assets/raviremmi.webp" alt="Raviremmi Logo" class="logo-image">
          <p>Kokoa ravihevosjoukkueesi ja kilpaile!</p>
        </div>
      </div>
    </header>

    <main class="main-content container">
      <ActiveNewsNotices />
      <router-view></router-view>
    </main>

    <Footer />

    <CookieNotice />
    <AiAssistantWidget v-if="showAiAssistant" />
  </div>
</template>

<script>
// Import necessary functions and libraries
import { ref, onMounted, onUnmounted, provide, computed } from 'vue'; // Added onUnmounted
import { useRouter } from 'vue-router';
import { Auth, Hub } from 'aws-amplify'; // Import Hub
import axios from './plugins/axios';
import CookieNotice from './components/CookieNotice.vue';
import Footer from './components/Footer.vue';
import AiAssistantWidget from './components/AiAssistantWidget.vue';
import ActiveNewsNotices from './components/ActiveNewsNotices.vue';
import { isAiAssistantAllowed } from './utils/aiAccess';

const DAY_IN_MS = 1000 * 60 * 60 * 24;
const CALENDAR_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;

const isValidCalendarDate = (calendarDate) => {
  if (!calendarDate) return false;

  const { year, month, day } = calendarDate;
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  return utcDate.getUTCFullYear() === year
    && utcDate.getUTCMonth() === month - 1
    && utcDate.getUTCDate() === day;
};

const parseCalendarDate = (dateValue) => {
  if (!dateValue) return null;

  const dateString = String(dateValue);
  const match = dateString.match(CALENDAR_DATE_RE);
  if (match) {
    const calendarDate = {
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
    };
    return isValidCalendarDate(calendarDate) ? calendarDate : null;
  }

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const calendarDate = {
    year: parsed.getUTCFullYear(),
    month: parsed.getUTCMonth() + 1,
    day: parsed.getUTCDate(),
  };
  return isValidCalendarDate(calendarDate) ? calendarDate : null;
};

const getHelsinkiCalendarDate = (date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('fi-FI', {
      timeZone: 'Europe/Helsinki',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);

    const values = {};
    parts.forEach((part) => {
      if (part.type !== 'literal') {
        values[part.type] = part.value;
      }
    });

    const calendarDate = {
      year: Number(values.year),
      month: Number(values.month),
      day: Number(values.day),
    };
    if (isValidCalendarDate(calendarDate)) {
      return calendarDate;
    }
  } catch (error) {
    console.error('Error reading Helsinki calendar date:', error);
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

const toCalendarDayNumber = (calendarDate) => (
  Math.floor(Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day) / DAY_IN_MS)
);

const compareCalendarDates = (firstDate, secondDate) => (
  toCalendarDayNumber(firstDate) - toCalendarDayNumber(secondDate)
);

const inclusiveCalendarDayCount = (startDate, endDate) => (
  toCalendarDayNumber(endDate) - toCalendarDayNumber(startDate) + 1
);

const formatCalendarDate = (calendarDate) => `${calendarDate.day}.${calendarDate.month}.`;

export default {
  name: 'App',
  components: {
    CookieNotice,
    Footer,
    ActiveNewsNotices,
    AiAssistantWidget
  },
  setup() {
    // --- Reactive State ---
    const isAuthenticated = ref(false); // Tracks user authentication status
    const authenticatedPlayerId = ref(null); // Stable PlayerID, falling back to Cognito sub
    const username = ref(''); // Stores the authenticated user's display name
    const router = useRouter(); // Access the Vue router instance
    
    // --- Dropdown State ---
    const dropdownOpen = ref(false);
    const userDropdown = ref(null);

    // --- Game Period State ---
    const gameStartDate = ref(null); // Start date of the current game period
    const gameEndDate = ref(null); // End date of the current game period
    const fetchError = ref(null); // Stores any error message during game period fetch
    const changeWindowState = ref(null); // Race-change window status for header info
    const currentTime = ref(new Date());
    let raceStatusIntervalId = null;

    // --- Function to Fetch Game Period Data ---
    const fetchGamePeriod = async () => {
        // Construct the API URL using environment variables or a fallback
        // IMPORTANT: Replace 'YOUR_API_GATEWAY_ENDPOINT' if not using env vars
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL || 'YOUR_API_GATEWAY_ENDPOINT'}/game-period`;
        if (apiUrl.includes('YOUR_API_GATEWAY_ENDPOINT')) {
            console.warn("API Gateway URL not set in environment variables (VUE_APP_API_BASE_URL). Using placeholder.");
            fetchError.value = 'API URL not configured.';
            return; // Stop if URL is missing
        }
        try {
            console.log("Fetching game period from:", apiUrl);
            const response = await fetch(apiUrl); // Fetch data from the API
            if (!response.ok) { // Check for HTTP errors
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json(); // Parse JSON response
            console.log("Received game period data:", data);

            // Validate received data
            if (data.startDate && data.endDate) {
                gameStartDate.value = data.startDate;
                gameEndDate.value = data.endDate;
                fetchError.value = null; // Clear previous errors
            } else {
                console.error("Invalid data format received from API:", data);
                throw new Error("Invalid data format received from API");
            }
        } catch (error) {
            console.error('Error fetching game period:', error);
            fetchError.value = 'Could not load game period data.';
            // Reset dates on error
            gameStartDate.value = null;
            gameEndDate.value = null;
        }
    };

    const formatHelsinkiDateTime = (isoString) => {
      const date = new Date(isoString);
      if (Number.isNaN(date.getTime())) {
        return '';
      }

      const parts = new Intl.DateTimeFormat('fi-FI', {
        timeZone: 'Europe/Helsinki',
        weekday: 'short',
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(date);

      const values = {};
      parts.forEach((part) => {
        if (part.type !== 'literal') {
          values[part.type] = part.value;
        }
      });

      if (!values.weekday || !values.day || !values.month || !values.hour || !values.minute) {
        return '';
      }

      const weekday = values.weekday.replace('.', '').toLowerCase();
      return `${weekday} ${values.day}.${values.month} klo ${values.hour}.${values.minute}`;
    };

    const fetchRaceStatus = async () => {
      const baseUrl = process.env.VUE_APP_API_BASE_URL || 'YOUR_API_GATEWAY_ENDPOINT';
      if (baseUrl.includes('YOUR_API_GATEWAY_ENDPOINT')) {
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/race-status`);
        const data = response.data;
        const fallbackReason = data?.gamePeriodStatus === 'ended'
          ? 'period_ended'
          : data?.gamePeriodStatus === 'future'
            ? 'period_not_started'
            : (data?.changesClosed ?? data?.racesOngoing)
              ? 'races_ongoing'
              : 'open';

        changeWindowState.value = {
          isClosed: data?.changeWindow?.isClosed ?? Boolean(data?.changesClosed ?? data?.racesOngoing),
          reason: data?.changeWindow?.reason || fallbackReason,
          nextCloseAt: data?.changeWindow?.nextCloseAt || data?.nextRaceTime || null,
        };
      } catch (error) {
        console.error('Error fetching race status for header:', error);
        changeWindowState.value = null;
      }
    };

    // --- Computed Properties for Game Display ---

    // Calculates the total number of days in the game period
    const totalGameDays = computed(() => {
        if (!gameStartDate.value || !gameEndDate.value) return 0; // Need both dates
        try {
            const start = parseCalendarDate(gameStartDate.value);
            const end = parseCalendarDate(gameEndDate.value);
            if (!start || !end) { // Validate parsed dates
                console.error("Invalid date format for calculation:", gameStartDate.value, gameEndDate.value);
                return 0;
            }

            if (compareCalendarDates(end, start) < 0) { // End date cannot be before start date
                console.warn("Game end date is before start date.");
                return 0;
            }

            // Count calendar days, not elapsed milliseconds, so DST changes cannot skew the result.
            const diffDays = inclusiveCalendarDayCount(start, end);
            return diffDays > 0 ? diffDays : 0; // Ensure non-negative result
        } catch (e) {
            console.error("Error calculating total game days:", e);
            return 0;
        }
    });

    // Calculates the current day number within the game period
    const currentGameDay = computed(() => {
        if (!gameStartDate.value || totalGameDays.value <= 0) return 0; // Need start date and valid total days
        
        try {
            const start = parseCalendarDate(gameStartDate.value);
            const end = gameEndDate.value ? parseCalendarDate(gameEndDate.value) : null;
            const today = getHelsinkiCalendarDate(currentTime.value);

            if (!start) { // Validate start date
                console.error("Invalid start date for current day calculation:", gameStartDate.value);
                return 0;
            }
            if (gameEndDate.value && !end) { // Validate end date if it exists
                 console.error("Invalid end date for current day calculation:", gameEndDate.value);
                 // Decide handling: Here, we proceed assuming game continues if end date is invalid
            }

            if (compareCalendarDates(today, start) < 0) {
                return 0; // Game hasn't started
            }
            
            if (end && compareCalendarDates(today, end) > 0) {
                return totalGameDays.value; // Game finished
            }

            // Calculate current day number (Day 1 is the start day)
            const diffDays = inclusiveCalendarDayCount(start, today);

            // Ensure current day doesn't exceed total days
            return Math.min(diffDays, totalGameDays.value);

        } catch(e) {
            console.error("Error calculating current game day:", e);
            return 0;
        }
    });

    // Provides a user-friendly string for the game progress display
    const gameProgressDisplay = computed(() => {
        if (fetchError.value) {
            return fetchError.value; // Show API fetch error
        }

        const current = currentGameDay.value;
        const total = totalGameDays.value;
        const today = getHelsinkiCalendarDate(currentTime.value);
        const start = gameStartDate.value ? parseCalendarDate(gameStartDate.value) : null;

        // Validate dates before displaying
        if (gameStartDate.value && !start) {
            return "Error: Invalid start date";
        }
        // Handle loading or invalid date scenarios
        if (total <= 0 && !fetchError.value) {
             return gameStartDate.value ? "Calculating game days..." : "Loading game period...";
        }

        // Format display string based on game state
        if (total > 0) {
            if (start && compareCalendarDates(today, start) < 0) { // Game hasn't started
                return `Alkaa ${formatCalendarDate(start)}`;
            } else if (current > 0) { // Game in progress or finished
                 return `Päivä ${current} / ${total}`;
            } else { // Fallback if calculation yields unexpected results
                 return `Total ${total} days`;
            }
        }

        return "Loading..."; // Default loading message
    });

    const changeWindowDisplay = computed(() => {
      if (!changeWindowState.value) {
        return '';
      }

      if (changeWindowState.value.reason === 'period_ended') {
        return 'Jakso päättynyt';
      }

      if (changeWindowState.value.isClosed) {
        if (changeWindowState.value.reason === 'races_ongoing') {
          return 'Ravit käynnissä';
        }

        return 'Vaihdot suljettu';
      }

      if (changeWindowState.value.nextCloseAt) {
        const formattedNextClose = formatHelsinkiDateTime(changeWindowState.value.nextCloseAt);
        if (formattedNextClose) {
          return `Vaihdot auki · sulkeutuu ${formattedNextClose}`;
        }
      }

      return 'Vaihdot auki';
    });

    const showAiAssistant = computed(() => (
      isAuthenticated.value
      && isAiAssistantAllowed({
        playerId: authenticatedPlayerId.value,
        username: username.value,
      })
    ));


    // --- Authentication Logic ---

    // Function to check the current user's authentication status with Amplify
    const checkUser = async (source = 'checkUser') => {
        console.log(`Checking user authentication status (source: ${source})...`);
        try {
            const user = await Auth.currentAuthenticatedUser(); // Check Amplify auth
            isAuthenticated.value = true;
            // Safely access attributes
            authenticatedPlayerId.value = user.attributes?.['custom:player_id'] || user.attributes?.sub;
            // Determine username (prioritize preferred_username which contains the chosen username)
            const tokenUsername = user.attributes?.preferred_username || user.username || user.attributes?.email || user.attributes?.sub || 'User';
            username.value = tokenUsername;

            // Cognito does not refresh an already-issued ID token immediately
            // after Google onboarding sets preferred_username. The Player row is
            // authoritative in that short window, so never expose Google_<sub>
            // as a public remmi name in the header.
            if (String(tokenUsername).startsWith('Google_')) {
              try {
                const profileResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/me`);
                if (profileResponse.data?.username) {
                  username.value = profileResponse.data.username;
                }
              } catch (_profileError) {
                // A brand-new Google identity intentionally has no profile until
                // it finishes onboarding on the callback route.
              }
            }
            console.log("User IS authenticated. PlayerID:", authenticatedPlayerId.value, "Username:", username.value);
        } catch (error) {
            isAuthenticated.value = false;
            authenticatedPlayerId.value = null;
            username.value = '';
            // Avoid logging the error in production unless needed for specific debugging
            console.log("User is NOT authenticated.", error.message || error);
        }
    };

    // Function to sign the user out using Amplify
    const signOut = async () => {
        try {
            await Auth.signOut();
            // State is updated via the Hub listener now, but setting here provides immediate feedback
            isAuthenticated.value = false;
            authenticatedPlayerId.value = null;
            username.value = '';
            router.push('/'); // Redirect to home page after sign out
            console.log("User signed out successfully.");
        } catch (error) {
            console.error('Error signing out:', error);
            // Optionally: show a user-facing error message
        }
    };

    // --- Amplify Hub Listener ---
    // Listens for Amplify authentication events (signIn, signOut, etc.)
    const hubListenerCancel = Hub.listen('auth', (data) => {
        switch (data.payload.event) {
            case 'signIn':
                console.log('Hub: user signed in');
                checkUser('hubSignIn'); // Re-check user details after sign in
                break;
            case 'signUp':
                console.log('Hub: user signed up');
                break;
            case 'signOut':
                console.log('Hub: user signed out');
                // Update state immediately on sign out event
                isAuthenticated.value = false;
                authenticatedPlayerId.value = null;
                username.value = '';
                 // Optional: Redirect here if not already handled elsewhere
                 if (router.currentRoute.value.meta.requiresAuth) {
                     router.push('/');
                 }
                break;
            case 'signIn_failure':
                console.error('Hub: user sign in failed', data.payload.data);
                break;
            case 'tokenRefresh':
                console.log('Hub: token refreshed');
                break;
            case 'tokenRefresh_failure':
                console.error('Hub: token refresh failed', data.payload.data);
                break;
            case 'configured':
                console.log('Hub: Amplify configured');
                break;
            default:
                 console.log('Hub: other auth event', data.payload.event)
                 break;
        }
    });

    // --- Navigation Functions ---
    const goToSignIn = () => { router.push('/sign-in'); };
    const goToSignUp = () => { router.push('/sign-up'); };

    // --- Dropdown Functions ---
    const toggleDropdown = () => {
        dropdownOpen.value = !dropdownOpen.value;
    };

    const closeDropdown = () => {
        dropdownOpen.value = false;
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
        if (userDropdown.value && !userDropdown.value.contains(event.target)) {
            closeDropdown();
        }
    };

    // Handle sign out from dropdown
    const handleSignOut = async () => {
        closeDropdown();
        await signOut();
    };

    const handleProfileCompleted = (event) => {
      if (event.detail?.username) {
        username.value = event.detail.username;
      }
      checkUser('googleProfileCompleted');
    };

    // --- Lifecycle Hooks ---
    onMounted(() => {
        console.log("App component mounted. Initializing checks.");
        checkUser('onMounted'); // Check auth status on initial load
        fetchGamePeriod(); // Fetch game period data on initial load
        fetchRaceStatus(); // Fetch race status for header info
        raceStatusIntervalId = window.setInterval(() => {
          currentTime.value = new Date();
          fetchRaceStatus();
        }, 60000);
        
        // Add click outside listener for dropdown
        document.addEventListener('click', handleClickOutside);
        window.addEventListener('raviremmi:profile-completed', handleProfileCompleted);
    });

    onUnmounted(() => {
        console.log("App component unmounted. Cleaning up Hub listener.");
        hubListenerCancel(); // Clean up the Hub listener when the component is destroyed
        
        // Remove click outside listener
        document.removeEventListener('click', handleClickOutside);
        window.removeEventListener('raviremmi:profile-completed', handleProfileCompleted);

        if (raceStatusIntervalId) {
          clearInterval(raceStatusIntervalId);
        }
    });

    // --- Provide Data to Descendants ---
    // Make authentication status and player ID available to child components via inject()
    provide('isAuthenticated', isAuthenticated);
    provide('authenticatedPlayerId', authenticatedPlayerId);

    // --- Return values exposed to the template ---
    return {
        isAuthenticated,
        username, // Expose username for display
        signOut,
        goToSignIn,
        goToSignUp,
        gameProgressDisplay, // Expose computed property for game progress display
        changeWindowDisplay, // Expose computed property for change-window display
        showAiAssistant,
        // Dropdown-related
        dropdownOpen,
        userDropdown,
        toggleDropdown,
        closeDropdown,
        handleSignOut,
    };
  },
};
</script>

<style>
/* Import base styles - these might be partially overridden below */
@import './assets/styles.css';

/* --- Global Styles & Resets for App.vue --- */
html, body {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: 'Roboto', Arial, sans-serif; /* Consider a more modern font stack if desired */
}

#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #0a192f; /* Dark blue background */
  color: #ccd6f6; /* Light text color */
}

.container {
  width: 100%;
  max-width: 1100px; /* Max width for content */
  margin: 0 auto; /* Center the container */
  padding: 0 20px; /* Horizontal padding */
  box-sizing: border-box; /* Include padding in width calculation */
}

/* --- Header Styling --- */
.main-header {
  /* Background image with gradient overlay */
  background: linear-gradient(rgba(10, 25, 47, 0.6), rgba(10, 25, 47, 0.9)), url('@/assets/hero2.jpg') no-repeat center center;
  background-size: cover;
  color: #ffffff; /* White text on header */
  position: relative; /* Needed for absolute positioning or z-index */
  width: 100%;
  /* Use CSS variable for height, defined later */
  height: var(--hero-h);
  display: flex;
  flex-direction: column;
  /* Default z-index is auto, stays behind positioned elements */
}

.top-bar {
  position: relative;
  z-index: 2; /* Keep top bar above background and main content */
  display: flex;
  justify-content: space-between; /* Space out nav and auth links */
  align-items: center; /* Vertically align items */
  padding-top: 15px;
  padding-bottom: 15px;
  flex-wrap: wrap; /* Allow items to wrap on smaller screens */
  gap: 15px; /* Add gap between flex items */
}

/* --- Navigation Styling (Top Left) --- */
.main-navigation {
  display: flex;
  gap: 20px; /* Space between nav links */
  flex-wrap: wrap; /* Allow links to wrap */
  align-items: center; /* Align items vertically */
}

.main-navigation a {
  text-decoration: none;
  color: #ccd6f6; /* Default link color */
  padding: 8px 0; /* Vertical padding */
  transition: color 0.3s ease, border-bottom-color 0.3s ease; /* Smooth transitions */
  font-size: 0.95rem;
  position: relative;
  border-bottom: 2px solid transparent; /* Underline effect on hover/active */
}

.main-navigation a:hover,
.main-navigation a.router-link-active { /* Style for active route */
  color: #ffffff; /* Brighter color on hover/active */
  border-bottom-color: #64ffda; /* Accent color underline */
  text-shadow: 0 0 8px rgba(100, 255, 218, 0.3); /* Subtle glow effect */
}

/* Remove default bolding/background for active links if not desired */
.main-navigation a.router-link-active {
  font-weight: normal;
  background-color: transparent;
}

/* --- Game Progress Display Styling (Center Top) --- */
.game-progress-display {
    font-size: 0.9em;
    color: #a8b2d1; /* Slightly dimmer text color */
    text-align: center;
    /* Add subtle background for better readability */
    background-color: rgba(17, 34, 64, 0.6);
    padding: 4px 10px;
    border-radius: 4px;
    min-width: 150px; /* Ensure some minimum width */
    white-space: nowrap; /* Prevent wrapping */
}

.header-status-stack {
  flex-grow: 1;
  min-width: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.change-window-display {
  font-size: 0.82em;
  color: #9fb7db;
  text-align: center;
  background-color: rgba(17, 34, 64, 0.45);
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
}


/* --- Auth Links Styling (Top Right) --- */
.auth-links {
  display: flex;
  align-items: center;
  gap: 15px; /* Space between auth buttons/username */
  /* margin-left: auto; Push auth links to the right - removed due to game progress */
  /* padding-left: 20px; Add padding if needed */
}

/* User Dropdown Styling */
.user-dropdown {
  position: relative;
  display: inline-block;
}

.username-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.3);
  color: #64ffda;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  min-width: 120px;
  justify-content: space-between;
  position: relative;
  z-index: 1001;
}

.username-button:hover {
  background: rgba(100, 255, 218, 0.15);
  border-color: rgba(100, 255, 218, 0.5);
  transform: translateY(-1px);
}

.username-button.dropdown-open {
  background: rgba(100, 255, 218, 0.2);
  border-color: #64ffda;
}

.username-text {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}

.dropdown-arrow {
  flex-shrink: 0;
  transition: transform 0.3s ease;
  opacity: 0.7;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: #112240;
  border: 1px solid #1e3a5f;
  border-radius: 8px;
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
  min-width: 180px;
  z-index: 10001;
  overflow: hidden;
  animation: dropdownFadeIn 0.2s ease-out;
  pointer-events: auto;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #ccd6f6;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  position: relative;
  z-index: 10000;
  pointer-events: auto;
}

.dropdown-item:hover {
  background: rgba(100, 255, 218, 0.1);
  color: #64ffda;
}

.dropdown-item.sign-out-item:hover {
  background: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
}

.dropdown-icon {
  flex-shrink: 0;
  opacity: 0.8;
}

.dropdown-divider {
  height: 1px;
  background: #1e3a5f;
  margin: 4px 0;
}

.username-display {
    color: #ccd6f6;
    font-size: 0.9rem;
    margin-right: 10px; /* Space between username and sign out button */
}

/* --- Header Content Wrapper (for positioning text/button) --- */
.header-content-wrapper {
  /* flex-grow: 1;  REMOVED - This was causing the overlap issue */
  display: flex;
  /* align-items: flex-start;  Reverted to original */
  justify-content: center; /* Center content horizontally */
  position: relative;
  z-index: 1; /* REDUCED - Lower than dropdown menu to prevent click interference */
  padding-top: 2%; /* Slightly increased padding for better spacing */
  height: auto; /* Added - Let height be determined by content + padding */
  /* Fixed height to prevent jumping on resize */
  min-height: 300px;
  @media (max-width: 992px) {
    min-height: 280px;
  }
  @media (max-width: 768px) {
    min-height: 260px;
    padding-top: 3%;
  }
  @media (max-width: 480px) {
    min-height: 240px;
    padding-top: 4%;
  }
}

/* --- Header Content (Centered Text/Button) --- */
.header-content {
  text-align: center;
  padding-bottom: 40px; /* Space below the button */
  /* width: 100%; Removed - Reverted to original */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* --- Logo Image Styling --- */
.logo-image {
  display: block; /* Make it a block element for centering */
  margin: 0 auto 15px auto; /* Center horizontally, add bottom margin */
  max-width: var(--logo-max-width); /* Use CSS variable for width */
  height: auto; /* Maintain aspect ratio */
  max-height: var(--logo-max-height); /* Use CSS variable for height */
}


.header-content p {
  /* Responsive font size */
  font-size: clamp(1rem, 3vw, 1.25rem);
  color: #a8b2d1; /* Slightly muted text color */
  margin-bottom: 10px; /* Reduced to prevent jumping */
}

/* --- Button Styles --- */
.btn {
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  background: none;
  font-weight: 500; /* Slightly bolder text */
}

.btn-outline {
  border-color: #64ffda; /* Accent color border */
  color: #64ffda; /* Accent color text */
}

.btn-outline:hover {
  background-color: rgba(100, 255, 218, 0.1); /* Subtle background on hover */
}

/* --- Main Content Area --- */
.main-content {
  flex-grow: 1; /* Allow main content to fill remaining space */
  /* Use CSS variable for negative margin */
  margin-top: calc(-1 * var(--hero-h) * var(--overlap-factor));
  position: relative; /* RESTORED - To establish stacking context */
  z-index: 2; /* ADDED - Place above header background, below header content */
  padding-top: 40px; /* Internal padding */
  padding-bottom: 40px;
  background-color: #0a192f; /* Ensure background is opaque */
  /* Optional: Add rounded corners to overlap nicely */
  border-top-left-radius: 15px;
  border-top-right-radius: 15px;
}

/* --- Overriding styles.css specifics if necessary --- */
/* Ensure container within #app doesn't get unwanted background */
#app .container {
  background-color: transparent;
}

/* Reset potentially conflicting header styles from styles.css */
header {
    text-align: left; /* Override centering if needed */
    margin-bottom: 0; /* Remove bottom margin */
    background-color: transparent !important; /* Ensure no background color is applied */
}
/* Remove h1 styling inheritance */
header .logo-image, header p {
    color: inherit; /* Inherit color from .main-header */
}

/* Remove body padding if styles.css adds it */
body {
    padding: 0;
}


/* --- Modal Styling Adjustments (To fit dark theme) --- */
/* Ensure these styles apply correctly if modals are used */
.modal-content {
  background-color: #112240; /* Darker blue for modal */
  color: #ccd6f6;
  border: 1px solid #1e3a5f; /* Subtle border */
  box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7); /* Dark shadow */
  position: relative;
  z-index: 1001; 
}

/* Styles for .modal-list-item in App.vue */
/* Ensure this does NOT re-introduce conflicting padding or display properties */
.modal-list-item {
  border-bottom: 1px solid #1e3a5f; /* Separator line - This is fine */
  /* padding: 12px 5px; */ /* CORRECTLY COMMENTED OUT / REMOVED */
  /* NO display: flex; or other layout-breaking padding here */
}

.modal-list-item:last-child {
  border-bottom: none; /* This is fine */
}

.modal-list-item h4 { /* These are fine as long as not overridden by more specific Tailwind in components */
  color: #ccd6f6;
  font-size: 1rem;
  margin-bottom: 5px;
}

.modal-list-item button { /* These are fine for buttons *not* heavily styled by Tailwind in components */
  background-color: #64ffda;
  color: #0a192f;
  border: none;
  padding: 6px 12px; 
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.modal-list-item button:hover {
  background-color: #52d6c0;
}


.close-modal {
  color: #8892b0; /* Muted color for close button */
  position: absolute; /* Position relative to modal-content */
  top: 15px;
  right: 20px;
  font-size: 1.8rem;
  line-height: 1; /* Adjust line height for better positioning */
  cursor: pointer;
  transition: color 0.3s ease;
  z-index: 1002; /* Ensure close button is above modal content */
}
.close-modal:hover {
  color: #64ffda; /* Accent color on hover */
}

/* Adjustments for list items from styles.css if they appear outside modals */
.list-item {
    background-color: #112240;
    color: #ccd6f6;
    border: 1px solid #1e3a5f;
    padding: 15px;
    margin-bottom: 10px;
    border-radius: 4px;
}
.list-item h3 {
    color: #ffffff;
    margin-top: 0;
    margin-bottom: 10px;
}
.list-item button {
    background-color: #64ffda;
    color: #0a192f;
    /* Inherit general button styles */
}
.list-item button:hover {
     background-color: #52d6c0;
}

/* Adjust add button from styles.css */
.add-button {
    background-color: #64ffda;
    color: #0a192f;
    padding: 10px 20px;
    /* Inherit general button styles */
}
.add-button:hover {
    background-color: #52d6c0;
}

/* Adjust confirmation buttons (e.g., in delete prompts) */
.confirmation-buttons {
    display: flex;
    gap: 10px;
    margin-top: 15px;
}
.confirmation-buttons button {
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    flex-grow: 1; /* Make buttons fill space if needed */
}
/* Confirm button (Yes) */
.confirmation-buttons button:first-of-type {
    background-color: #64ffda; /* Accent */
    color: #0a192f;
    border: 1px solid #64ffda;
}
/* Cancel button (No) */
.confirmation-buttons button:last-of-type {
    background-color: transparent;
    color: #ff6b6b; /* Red for cancel/danger */
    border: 1px solid #ff6b6b;
}
.confirmation-buttons button:first-of-type:hover {
   background-color: #52d6c0;
   border-color: #52d6c0;
}
.confirmation-buttons button:last-of-type:hover {
   background-color: rgba(255, 107, 107, 0.1); /* Subtle red background */
}

/* ---------- CSS Variables for Header Overlap Effect ---------- */
:root {
  /* Define hero height: 16:9 aspect ratio, clamped between 320px and 760px */
  --hero-h: clamp(320px, 56.25vw, 760px);

  /* Overlap factor: determines how much main content overlaps header */
  /* 0.62 means overlap by 62% of the header height */
  --overlap-factor: 0.62;
  
  /* Button and logo positioning variables */
  --logo-max-width: 60%;
  --logo-max-height: 180px;
  --button-position: 30px;
}

/* ---------- Apply Variables ---------- */
.main-header {
  /* Use the variable for height */
  height: var(--hero-h);
}

/* .main-content rule is modified above */

/* ---------- Responsive Adjustments for Overlap ---------- */
/* Reduce overlap on medium-large screens */
@media (max-width: 1400px) {
  :root { 
    --overlap-factor: 0.55; 
    --logo-max-width: 60%;
    --logo-max-height: 180px;
    --button-position: 30px;
  }
}
/* Further reduce overlap on medium screens */
@media (max-width: 1200px) {
  :root { 
    --overlap-factor: 0.48; 
    --logo-max-width: 65%;
    --logo-max-height: 170px;
    --button-position: 28px;
  }
}
/* Significantly reduce overlap on smaller tablets */
@media (max-width: 992px) {
  :root { 
    --overlap-factor: 0.32; 
    --logo-max-width: 70%;
    --logo-max-height: 160px;
    --button-position: 25px;
  }
}
/* Medium-small screens */
@media (max-width: 805px) {
  :root { 
    --overlap-factor: 0; 
    --logo-max-width: 80%;
    --logo-max-height: 140px;
    --button-position: 22px;
  }
  .main-content {
      /* Reset rounded corners when not overlapping */
      border-top-left-radius: 0;
      border-top-right-radius: 0;
  }
}
/* Small screens (mobile) */
@media (max-width: 480px) {
  :root { 
    --logo-max-width: 90%;
    --logo-max-height: 120px;
    --button-position: 20px;
  }
}

/* Mobile Grid Layout for Top Bar */
@media (max-width: 805px) {

/* Wipe any flex/grid left over from the desktop rules so we start clean.   */
.top-bar,
.main-navigation,
.auth-links,
.header-status-stack { all: unset; }

/* ------------- TOP‑BAR GRID --------------------------------------------- */
.top-bar{
  display: grid;
  grid-template-columns: 1fr auto;        /* stretch → fixed‑width auth      */
  grid-template-areas:
    "progress auth"
    "nav      nav";
  row-gap: 12px;
  column-gap: 10px;
  align-items: center;
  padding: 14px 0;
  width: 100%;
}

/* --- Progress ----------------------------------------------------------- */
.header-status-stack{
  grid-area: progress;
  justify-self: start;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
}

.game-progress-display{
  font-size: .9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.change-window-display{
  font-size: .8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* --- Auth (username + button) ------------------------------------------ */
.auth-links{
  grid-area: auth;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.username-display{
  font-size: .85rem;
}
.auth-links .btn{
  padding: 6px 10px;
  font-size: .9rem;
  flex: 0 0 auto;
  max-width: 140px;
}

/* --- Nav links grid ----------------------------------------------------- */
.main-navigation{
  grid-area: nav;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: 10px 15px;              /* row / column gap */
  width: 100%;
  justify-items: center;
  overflow-x: hidden;          /* belt‑and‑braces: no side‑scroll       */
}
.main-navigation a{
  font-size: .9rem;
  white-space: nowrap;
  padding: 6px 0;
}

/* --- Give the hero copy some breathing‑room ---------------------------- */
/* Adjusted padding for the modified wrapper - Reverted to original mobile padding */
.header-content-wrapper{ 
  padding-top: 60px; /* Reduced padding for mobile */
  min-height: 220px; /* Ensure consistent height on mobile */
}
}

/* **** MODAL Z-Index Fix (Values kept high) **** */
/* Target common modal wrapper/backdrop class names */
/* Adjust class names if your modal components use different ones */
.modal-overlay,
.modal-backdrop,
.modal { /* Added .modal as another potential target */
  position: fixed; /* Position relative to the viewport */
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000; /* Higher than header-content-wrapper (5) */
  /* Background color (e.g., rgba(0, 0, 0, 0.5)) is likely set within the modal component itself */
}

/* Ensure modal content is positioned correctly relative to its backdrop */
.modal-content {
    position: relative; /* Keeps it within the backdrop flow */
    z-index: 1001; /* Ensure content is above backdrop */
}

/* Ensure close button is above modal content */
.close-modal {
  z-index: 1002; /* Ensure close button is above modal content */
}
/* **** END: Modal Z-Index Fix **** */


</style>
